import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { isAxiosError } from "axios";
import { ArrowLeft, CalendarX, Check, MessageSquare, PenLine, Sparkles, TriangleAlert } from "lucide-react";
import DashboardPage from "@/components/DashboardPage";
import { Button } from "@/components/form/Button";
import { usePermissions } from "@/hooks/usePermissions";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGetBrands } from "@/hooks/useGetBrands";
import { cn } from "@/utils/helpers";
import {
  MEDIA_TYPE_LABEL,
  PLAN_BRIEF_EDITABLE_STATUSES,
  displayAspectRatio,
  isPlanFrozen,
  openDeclineFor,
  slotHasBrief,
  type CalendarPlanSlot,
  type CalendarPlatform,
  type CalendarSlotIdea,
  type CalendarMonthPlanWithSlots,
} from "@/types/contentCalendar.type";
import { BRIEF_EDITABLE_STATUSES, liveBody, type BriefComment, type BriefRailInfo } from "@/types/productionBrief.type";
import { queue } from "@/components/Toast";
import { useAuthStore } from "@/stores/authStore";
import { useBrandIdentity } from "@/app/routes/dashboard/data/useCreativeOsHome";
import { formatSlotSchedule } from "../calendar.utils";
import { useSlotIdeation, useTeamApproveSlot } from "../api/useSlotIdeation";
import { useCalendarSettings, useDeletePlanSlot } from "../api/useContentCalendar";
import { useConceptVersions } from "../api/useConceptVersions";
import {
  useBriefRevisions,
  useRecallSlotBrief,
  useRetryBriefGeneration,
  useSendBriefsForReview,
  useSlotBrief,
  useTeamAcceptBrief,
} from "../api/useProductionBrief";
import DeleteSlotModal from "../components/DeleteSlotModal";
import { EditPlatformsModal } from "../components/DistributionPanel";
import SendToClientModal from "../components/SendToClientModal";
import { exportSeatBriefPdf } from "../components/exportSeatBriefPdf";
import DateRail from "../components/DateRail";
import PostHeader, { type BriefVersionOption, type PostPhase } from "../components/PostHeader";
import StatusBanner, { type PostViewport } from "../components/StatusBanner";
import MovePostDialog from "../month/MovePostDialog";
import RecordApprovalDialog from "../review/RecordApprovalDialog";
import { isReminderCooldown, useRemindClient } from "../review/useClientRoundActions";
import { acceptedIdea, anyMachineWork, canMoveSlot, canTeamAcceptBrief, slotDeleteBlocked } from "../slotStage";
import {
  briefDeclineReasonFrom,
  briefSentAtFrom,
  clientLabel,
  clientTitle,
  derivePostState,
  monthCountLine,
  shortDate,
  shortReason,
  type PostBanner,
  type PostState,
  type PrimaryAction,
} from "../postState";
import { useStuckClock } from "../useStuckClock";
import { buildPillarHueMap } from "../../strategy/components/strategyMeta";
import { railTabFrom, withRailTab } from "../../agent/railCommentsStore";
import { agentRail } from "../../agent/agentFocusStore";
import { KitModal } from "../../components/kit/Modal";
import { usePostComments } from "../comments/usePostComments";
import PostComments from "../comments/PostComments";
import ConceptItemWorkspace from "./ConceptItemWorkspace";
import LiveView, { LiveSkeleton } from "../live/LiveView";
import { ProductionBriefSkeleton } from "../components/ProductionBriefDoc";
import LiveComments from "../live/LiveComments";
import { useLiveStage } from "../live/useLiveStage";
import { bannerInput } from "../live/liveModel";
import ConceptDraftEditor from "./ConceptDraftEditor";
import { reopenedConceptIdea } from "./conceptReviewModel";
import IdeaMediaDialog, { referenceFromChoice, shortIdeaName, type MediaChoice } from "./IdeaMediaDialog";
import { CalendarTopLane } from "../month/PhoneCalendar";
import EditorialConceptBoard, {
  type IdeaReworkSource,
  EditorialConceptSkeletons,
  type EditorialConceptActions,
} from "./EditorialConceptBoard";
import IdeaReferenceCard from "./IdeaReferenceCard";
import IdeaSlideBoard, { IdeaSlideBoardWriting } from "./IdeaSlideBoard";
import { IdeaSlideContext, ideaSlideData } from "./IdeaSlide";
import WriteOwnIdeaPanel, { EMPTY_WRITE_OWN, type WriteOwnDraft } from "./WriteOwnIdeaPanel";
import { nextWalkPost, walkToast } from "./ideaCardModel";

/**
 * The post page: the date rail, the post header, one status banner saying whose
 * turn it is, then the concept, the creative brief or Distribution below. The
 * reference journey supplies the information architecture only; the surface
 * stays inside the Creative OS cockpit design system.
 */

type Move = { slotId: string; day: number; position: number };

const NO_BRIEF_COMMENTS: BriefComment[] = [];

/** A brief is only asked about once the client has approved the concept. */
function railBriefFor(briefs: Map<string, BriefRailInfo>, slot: CalendarPlanSlot) {
  // The brief exists from the pick on: the idea and its brief go to the client together.
  return slotHasBrief(slot) ? (briefs.get(slot.id) ?? null) : undefined;
}

const STAGE_WORD = { concept: "Concept", production: "Approved", live: "Live" } as const;

/** The phone's meta line: "Video 9:16 · Concept, ready to send". */
function phoneMeta(slot: CalendarPlanSlot, state: PostState, isGuest: boolean): string {
  const format = `${MEDIA_TYPE_LABEL[slot.mediaType]} ${displayAspectRatio(slot)}`;
  const turn = state.turn.label.charAt(0).toLowerCase() + state.turn.label.slice(1);
  // The client's side never says Production; a quick post never says Approved.
  const stage = isGuest ? "Idea and brief" : state.quick && state.stage !== "live" ? "Files" : STAGE_WORD[state.stage];
  return `${format} · ${stage}, ${turn}`;
}

function EmptyConcepts({ onBack }: { onBack: () => void }) {
  return (
    <div className="border-t border-co-line px-[26px] py-16 sm:py-20">
      <h2 className="tt-title-l font-semibold uppercase text-co-text">No posts yet</h2>
      <p className="tt-read-m mt-3 max-w-[58ch] text-co-muted">
        Build the month on the calendar first. The agents will then prepare three
        ideas for every planned date.
      </p>
      <Button
        size="small"
        variant="secondary"
        buttonStyle="outline"
        icon={ArrowLeft}
        className="mt-6"
        onPress={onBack}
      >
        Back to calendar
      </Button>
    </div>
  );
}

export default function ConceptsView({
  brandId,
  plan,
  planId,
  year,
  month,
  slots,
  pillars,
  briefBySlot,
  selectedSlotId,
  onSelect,
  onBack,
  onPreviewAsClient,
  onShare,
  phase = "concept",
  onPhaseChange,
  onRecall,
  recalling = false,
  onMoveSlot,
}: {
  brandId: string;
  plan: CalendarMonthPlanWithSlots;
  planId: string;
  year: number;
  month: number;
  slots: CalendarPlanSlot[];
  pillars?: readonly { id: string; name: string; order?: number; whyItMatters?: string }[];
  /** The month's briefs by post: what the date rail's turns and tracks read. */
  briefBySlot: Map<string, BriefRailInfo>;
  selectedSlotId: string | null;
  onSelect: (slotId: string) => void;
  onBack: () => void;
  onPreviewAsClient: (slotId: string) => void;
  onShare: (slotId: string) => void;
  phase?: PostPhase;
  onPhaseChange: (phase: PostPhase) => void;
  /** Take this post back off the client's board: the everyday undo for a share. */
  onRecall?: (slotId: string) => void;
  recalling?: boolean;
  /** Move a post to another day (the month's move, with its Undo toast). */
  onMoveSlot?: (move: Move) => Promise<void>;
}) {
  const { canEdit, isGuest } = usePermissions();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const viewport: PostViewport = isPhone ? "phone" : isTablet ? "tablet" : "desktop";
  const brandsQuery = useGetBrands();
  const clientName = brandsQuery.data?.find((brand) => brand.id === brandId)?.name ?? null;
  const ideation = useSlotIdeation(planId);
  const [mediaIdeaId, setMediaIdeaId] = useState<string | null>(null);
  // The hand-written concept form, per post. `fields` holds what was last
  // submitted so a failed save reopens the form with the draft intact.
  // Write your own idea on a post with no ideas yet: the same form as beside the three.
  const [manualDraft, setManualDraft] = useState<{ slotId: string; draft: WriteOwnDraft } | null>(null);
  // Select, then Pick: the selected idea, per post.
  const [selectedIdea, setSelectedIdea] = useState<{ slotId: string; ideaId: string } | null>(null);
  // An older brief version being read, per post.
  const [viewing, setViewing] = useState<{ slotId: string; version: number } | null>(null);
  // Bumped by "Edit the brief"; reset whenever the tab or the post changes.
  const [editBriefRequest, setEditBriefRequest] = useState<{ slotId: string; n: number } | null>(null);
  const [editIdeaRequest, setEditIdeaRequest] = useState<{ slotId: string; n: number } | null>(null);
  const [pullBackOpen, setPullBackOpen] = useState(false);
  const [sendBriefOpen, setSendBriefOpen] = useState(false);
  // Write your own idea, per post: the panel stands in place of the board.
  const [writeOwn, setWriteOwn] = useState<{ slotId: string; draft: WriteOwnDraft } | null>(null);
  // The manual editor for one idea's words (the card's more menu).
  const [editingWords, setEditingWords] = useState<{ slotId: string; ideaId: string } | null>(null);
  // Ideas whose words the team saved here this session: New angle asks first.
  const [editedIdeaIds, setEditedIdeaIds] = useState<ReadonlySet<string>>(() => new Set());
  const [confirmAngle, setConfirmAngle] = useState<{ slotId: string; ideaId: string } | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const ordered = useMemo(
    () =>
      slots
        .filter((slot) => (isGuest ? slot.origin !== "DIRECT" : true))
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt) || a.order - b.order),
    [isGuest, slots],
  );
  const pillarColors = useMemo(() => {
    return buildPillarHueMap(
      pillars ?? [],
      slots.map((slot) => slot.pillarId),
    );
  }, [pillars, slots]);
  const selected =
    ordered.find((slot) => slot.id === selectedSlotId) ??
    ordered.find((slot) => slot.status === "IDEATION") ??
    ordered[0] ??
    null;
  const selectedIndex = selected ? ordered.findIndex((slot) => slot.id === selected.id) : -1;
  const selectedPillarHue = selected?.pillarId
    ? pillarColors.get(selected.pillarId)
    : undefined;
  const now = useStuckClock(anyMachineWork(ordered, briefBySlot));
  const mediaIdea = selected?.ideas?.find((idea) => idea.id === mediaIdeaId) ?? null;
  const winner = selected ? acceptedIdea(selected) : null;
  const reopenedIdea = selected ? reopenedConceptIdea(selected) : null;
  const openDecline = openDeclineFor(selected);
  const deleteSlot = useDeletePlanSlot(brandId, planId);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canDelete = canEdit && !!selected && !isPlanFrozen(plan.status);
  const deleteBlocked = !!selected && slotDeleteBlocked(selected);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  // The server refuses a platform change while the post is with the client.
  const canEditPlatforms =
    canEdit && !isGuest && !!selected && selected.status !== "IN_CLIENT_REVIEW" && !isPlanFrozen(plan.status);
  const editPlatforms = canEditPlatforms ? () => setPlatformsOpen(true) : undefined;

  // The open post's brief in full (its error, its source) and its versions,
  // for the banner's words and Version ▾. Both dedupe with the brief tab's.
  const clientApproved = selected?.status === "CLIENT_APPROVED";
  // A quick post has no concept and no brief. The page reads it as both already accepted.
  const direct = selected?.origin === "DIRECT";
  const approved = clientApproved || direct;
  // Creo writes the brief the moment an idea is picked; it goes out with the concept.
  const hasBrief = !!selected && slotHasBrief(selected);
  const briefQuery = useSlotBrief(selected?.id ?? null, hasBrief, plan.status, selected?.status);
  const fullBrief = hasBrief ? (briefQuery.data?.brief ?? null) : null;
  const revisionsQuery = useBriefRevisions(
    fullBrief?.id ?? null,
    !isGuest && !!fullBrief && (fullBrief.version > 1 || !!fullBrief.sentVersion || fullBrief.status === "CHANGES_REQUESTED"),
  );
  const revisions = useMemo(
    () => (fullBrief ? (revisionsQuery.data?.revisions ?? []) : []),
    [fullBrief, revisionsQuery.data?.revisions],
  );
  const conceptVersions = useConceptVersions(selected?.status === "IN_CLIENT_REVIEW" ? selected.id : null);
  const conceptSentAt = useMemo(() => {
    const versions = conceptVersions.data?.versions ?? [];
    const latest = [...versions].sort((a, b) => b.version - a.version)[0];
    return latest?.sharedAt ?? null;
  }, [conceptVersions.data?.versions]);
  const sendBriefs = useSendBriefsForReview(planId);
  const retryBrief = useRetryBriefGeneration(planId, selected?.id ?? null);
  const recallBrief = useRecallSlotBrief(planId, selected?.id ?? null);
  const teamApproveSlot = useTeamApproveSlot(planId);
  const teamAcceptBrief = useTeamAcceptBrief(planId, selected?.id ?? null);
  // The post whose "Approve without the client" is being confirmed.
  const [teamApproveFor, setTeamApproveFor] = useState<string | null>(null);
  // Approve for the client on record (Figma 08: the client did not answer by the reply-by day),
  // and the outside approval it can be recorded as instead.
  const [approveForOpen, setApproveForOpen] = useState(false);
  const [recordOutsideOpen, setRecordOutsideOpen] = useState(false);
  const remind = useRemindClient(planId);
  const myName = useAuthStore((state) => state.user?.name)?.trim() || null;
  const brand = useBrandIdentity();
  const [exporting, setExporting] = useState(false);

  // Comments sit beside the chat for the team (the rail on the desk, a sheet
  // on a tablet or phone); the open tab or sheet lives in ?rail=comments.
  const [searchParams, setSearchParams] = useSearchParams();
  const commentsOpen = railTabFrom(searchParams) === "comments";
  const setCommentsOpen = (open: boolean) =>
    setSearchParams((previous) => withRailTab(previous, open ? "comments" : "chat"), { replace: true });
  const comments = usePostComments({
    slot: selected,
    planId,
    brief: fullBrief,
    briefComments: hasBrief ? (briefQuery.data?.comments ?? NO_BRIEF_COMMENTS) : NO_BRIEF_COMMENTS,
    canEdit,
    enabled: !isGuest,
  });
  const commentsEntry =
    !isGuest && viewport !== "desktop" ? { count: comments.count, onOpen: () => setCommentsOpen(true) } : undefined;

  const states = useMemo(() => {
    const map = new Map<string, PostState>();
    for (const slot of ordered) {
      map.set(slot.id, derivePostState(slot, { brief: railBriefFor(briefBySlot, slot), clientName, isGuest, now }));
    }
    return map;
  }, [ordered, briefBySlot, clientName, isGuest, now]);

  const sortedIdeas = useMemo(
    () => [...(selected?.ideas ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [selected?.ideas],
  );
  // The idea slide's words come from the idea itself (objective claim, sources); the page adds the brand and pillar hue.
  const slideFor = (idea: CalendarSlotIdea, index: number) =>
    ideaSlideData(selected!, idea, {
      brandName: clientName?.trim() || "",
      pillarHue: selectedPillarHue ?? null,
      index,
      total: sortedIdeas.length,
    });
  const pickedIdeaId =
    selected && selectedIdea?.slotId === selected.id && sortedIdeas.some((idea) => idea.id === selectedIdea.ideaId)
      ? selectedIdea.ideaId
      : // The slide always shows an idea, so the one on it is the one Pick idea N picks (Figma 08).
        viewport !== "phone" && selected?.status === "IDEATION"
        ? (sortedIdeas.find((idea) => idea.status !== "WORKING")?.id ?? null)
        : null;

  // Live (DESIGN.md "Live"): only an approved post with an accepted brief has
  // it. The page owns the stage so the banner and the tab read the same state.
  const briefAccepted =
    direct || (fullBrief?.status ?? briefBySlot.get(selected?.id ?? "")?.status) === "ACCEPTED";
  // The client (a guest) has no Kit until the team uploaded a file and it is
  // ready; the server refuses their Kit read before that too.
  const guestKitReady = selected?.deliverable?.status === "READY";
  const showDistribution = approved && briefAccepted && (!isGuest || guestKitReady);
  const settingsQuery = useCalendarSettings(brandId);
  const liveStage = useLiveStage({
    slot: selected,
    // The team reads it for the banner on every tab; a guest only on the Live
    // tab, as the old Distribution panel did.
    enabled: showDistribution && (!isGuest || phase === "publishing"),
    timeZone: settingsQuery.data?.timezone ?? "UTC",
  });
  const liveRead =
    showDistribution && liveStage && !liveStage.loading && !liveStage.error && (phase === "publishing" || !!selected?.deliverable)
      ? bannerInput(liveStage.data, liveStage.uploads)
      : null;

  const selectedState: PostState | null = selected
    ? derivePostState(selected, {
        brief: hasBrief ? (fullBrief ?? railBriefFor(briefBySlot, selected)) : undefined,
        clientName,
        isGuest,
        now,
        selectedIdea: pickedIdeaId
          ? { id: pickedIdeaId, index: sortedIdeas.findIndex((idea) => idea.id === pickedIdeaId) }
          : null,
        conceptSentAt,
        briefSentAt: briefSentAtFrom(revisions),
        briefDeclineReason: fullBrief ? briefDeclineReasonFrom(revisions, fullBrief.sentVersion) : null,
        live: liveRead,
      })
    : null;
  const movable = !!onMoveSlot && canEdit && !!selected && canMoveSlot(selected) && !isPlanFrozen(plan.status);
  // Only someone who can act sees the primary. A missed post that cannot be
  // moved right now (a frozen or planning month) loses its Move primary, and
  // the meta then claims only what is true.
  const sharedBanner = !selectedState
    ? null
    : !(canEdit || isGuest)
      ? { ...selectedState.banner, primary: undefined }
      : selectedState.banner.primary?.action === "move-post" && !movable
        ? { ...selectedState.banner, primary: undefined, meta: "Concept · The day passed before an idea was picked" }
        : // A Live primary the stage cannot run draws no button (liveStage.ts).
          (selectedState.banner.primary?.action === "send-kit" && !liveStage?.sendKit) ||
            (selectedState.banner.primary?.action === "copy-kit-link" && !liveStage?.copyKitLink) ||
            (selectedState.banner.primary?.action === "add-files" && !liveStage?.addFiles)
          ? { ...selectedState.banner, primary: undefined }
          : selectedState.banner;

  // Two states in the Pen's own words (d4Ec4, thWTL). The day passed: the date,
  // what did not happen, and a Move that keeps the ideas. The brief being
  // written: which idea it is written from, with its clock, under a banner.
  const missedDay = selected?.status === "MISSED" ? shortDate(selected.scheduledAt) : null;
  const winnerIndex = winner ? sortedIdeas.findIndex((idea) => idea.id === winner.id) : -1;
  const railBrief = selected ? (fullBrief ?? briefBySlot.get(selected.id) ?? null) : null;
  const briefWriting =
    !!selected && hasBrief && !!winner && !isGuest && (!railBrief || railBrief.status === "GENERATING");
  const banner: PostBanner | null = !sharedBanner
    ? null
    : briefWriting
      ? phase === "brief"
        ? // Figma 08, the brief being written: about a minute, and what to do if it stalls.
          {
            icon: "Loader",
            title: "Creo is writing the creative brief · about a minute",
            meta: "You can leave: the card and Home say when it’s ready. Not done after 3 minutes? Try again, or pick another idea.",
          }
        : // Figma 08, idea picked: which idea the brief is written from, with its clock.
          {
            icon: "Loader",
            title: winnerIndex >= 0 ? `Creo writes the brief from idea ${winnerIndex + 1}` : "Creo writes the brief",
            meta: "Concept · You can leave. The card and Home say when it’s ready.",
          }
      : sharedBanner;
  const bannerSince = briefWriting ? (fullBrief?.createdAt ?? selected?.updatedAt ?? null) : null;
  const [missedIdeasOpen, setMissedIdeasOpen] = useState<string | null>(null);
  const missedIdeasShown = !!selected && missedIdeasOpen === selected.id;
  // Where each rewrite this page started came from, for the rewriting card's note.
  const [reworkFrom, setReworkFrom] = useState<Record<string, IdeaReworkSource>>({});
  const markRework = (ideaId: string, from: IdeaReworkSource) =>
    setReworkFrom((previous) => ({ ...previous, [ideaId]: from }));

  const writingOwn = !!selected && writeOwn?.slotId === selected.id && selected.status === "IDEATION" && canEdit;

  const needYou = [...states.values()].filter((state) => state.turn.owner === "you").length;
  const monthName = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    timeZone: "UTC",
  });

  const briefExists = direct || (hasBrief && (!!fullBrief || !!briefBySlot.get(selected?.id ?? "")));

  // Whether the brief and Live tabs exist hangs on the post's brief. Until
  // either read of it lands, a link to those tabs holds its tab (as a skeleton)
  // rather than painting the Concept tab first and jumping across.
  const briefKnown =
    !selected || direct || !hasBrief || briefQuery.data !== undefined || briefBySlot.has(selected.id);
  const laterPhase = phase === "brief" || phase === "upload" || phase === "publishing" || phase === "distribution";
  const phasePending = laterPhase && !briefKnown && !briefQuery.isError;

  // Only an approved post with an accepted brief has Live.
  // An old Upload or Publishing link reads Live.
  const resolvedPhase: PostPhase = phasePending
    ? phase === "brief" ? "brief" : "publishing"
    : (phase === "upload" || phase === "publishing" || phase === "distribution") && !showDistribution
      ? "concept"
      : phase === "upload" || phase === "distribution"
        ? "publishing"
        : phase === "brief" && !briefExists
          ? "concept"
          : phase;
  const activePhase: PostPhase =
    direct && (resolvedPhase === "concept" || resolvedPhase === "brief")
      ? "publishing"
      : resolvedPhase;
  const versionOptions: BriefVersionOption[] = useMemo(
    () =>
      [...revisions]
        .sort((a, b) => b.version - a.version)
        .map((entry) => ({
          version: entry.version,
          createdAt: entry.createdAt,
          sent: (entry.sentInRounds?.length ?? 0) > 0 || (entry.sentRounds?.length ?? 0) > 0,
        })),
    [revisions],
  );
  const viewingVersion =
    selected && viewing?.slotId === selected.id && viewing.version !== fullBrief?.version ? viewing.version : null;
  const readOnlyRevision = viewingVersion
    ? (revisions.find((entry) => entry.version === viewingVersion) ?? null)
    : null;

  // The brief's Print and Recall live in the header's more menu (the banner is
  // the only Send). Same export and mutation the brief tab used.
  const briefBody = liveBody(fullBrief);
  const canPrintBrief = !!fullBrief && !!briefBody && !!winner;
  const printBrief = async () => {
    if (!selected || !fullBrief || !briefBody || !winner || exporting) return;
    setExporting(true);
    try {
      await exportSeatBriefPdf(
        selected,
        winner,
        {
          brandName: brand.name,
          preparedBy: brand.curatorName || undefined,
          documentVersion: fullBrief.version,
        },
        { kind: fullBrief.kind, status: fullBrief.status, body: briefBody },
      );
    } catch (error) {
      console.error("Production brief PDF export failed:", error);
      queue.add({ title: "Could not export the brief", description: "Please try again.", variant: "error" });
    } finally {
      setExporting(false);
    }
  };
  const canRecallBrief = canEdit && fullBrief?.status === "IN_REVIEW";

  // Approve without the client: the team answers for them, before the send or
  // while it waits on them. The server pulls the post back off their page
  // first, and an answer they already gave wins (409). The idea and its brief
  // are one answer ("concept"), so the brief must be written; "brief" alone is
  // the old brief loop of a post approved before the two were merged.
  const briefWritten = !!railBrief && ["DRAFT", "IN_REVIEW", "CHANGES_REQUESTED", "ACCEPTED"].includes(railBrief.status);
  const teamGate: "concept" | "brief" | null =
    !canEdit || !selected
      ? null
      : !!winner &&
          briefWritten &&
          !isPlanFrozen(plan.status) &&
          (selected.status === "SIGNED_OFF" || selected.status === "IN_CLIENT_REVIEW")
        ? "concept"
        : approved &&
            (PLAN_BRIEF_EDITABLE_STATUSES as string[]).includes(plan.status) &&
            canTeamAcceptBrief(fullBrief)
          ? "brief"
          : null;
  const teamApproveBusy = teamApproveSlot.isPending || teamAcceptBrief.isPending;
  const client = clientLabel(clientName);
  const Client = clientTitle(clientName);
  // Someone answered first (contract rule 12): the approve came back 409
  // `client_answered`, and the post's fresh decision says who and when.
  const [answeredFirst, setAnsweredFirst] = useState<string | null>(null);
  const clientAnswered = (err: unknown) =>
    isAxiosError(err) && err.response?.status === 409 && (err.response.data as { code?: string } | undefined)?.code === "client_answered";
  const noteAnsweredFirst = (slotId: string) => ({
    onError: (err: unknown) => {
      if (clientAnswered(err)) setAnsweredFirst(slotId);
    },
  });
  const answeredFirstLine = (() => {
    if (!selected || answeredFirst !== selected.id) return null;
    const decision = selected.reviewDecisions?.[0];
    if (!decision) return `${Client} answered this first.`;
    const at = (decision as { decidedAt?: string | null }).decidedAt;
    const clock = at ? new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : null;
    return `${decision.clientName?.trim() || Client} ${decision.decision === "APPROVED" ? "approved this" : "asked for changes"} first${clock ? `, at ${clock}` : ""}.`;
  })();
  const confirmTeamApproval = () => {
    setTeamApproveFor(null);
    if (!selected || !teamGate) return;
    if (teamGate === "concept") teamApproveSlot.mutate(selected.id, noteAnsweredFirst(selected.id));
    else teamAcceptBrief.mutate(undefined, noteAnsweredFirst(selected.id));
  };

  const changePhase = (next: PostPhase) => {
    if (selected?.origin === "DIRECT" && (next === "concept" || next === "brief")) return;
    setEditBriefRequest(null);
    if (next !== "brief") setViewing(null);
    onPhaseChange(next);
  };

  const confirmDelete = () => {
    if (!selected) return;
    // The post leaves the cache on confirm, so move on now: waiting for the
    // response would leave the date rail falling back to some other post.
    const neighbour = ordered[selectedIndex + 1] ?? ordered[selectedIndex - 1] ?? null;
    deleteSlot.mutate(selected.id);
    setDeleteOpen(false);
    if (neighbour) onSelect(neighbour.id);
    else onBack();
  };

  // Select, then Pick: the header's "Pick idea N" is the one path, and a pick
  // is named in its toast. There is no undo API for a pick, so no Undo.
  // On the batch walk (?walk=ideas) the page moves on to the next post that
  // still waits for a pick, and the toast names it.
  const acceptIdea = ideation.acceptIdea;
  const walking = searchParams.get("walk") === "ideas";
  const pickIdea = (slotId: string, ideaId: string) => {
    const index = sortedIdeas.findIndex((idea) => idea.id === ideaId);
    const next = walking ? nextWalkPost(ordered, slotId) : null;
    acceptIdea.mutate({
      slotId,
      ideaId,
      pickedTitle: index >= 0 ? walkToast(index, next) : undefined,
    });
    if (!walking) return;
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (next) {
          params.set("slot", next.id);
          params.delete("phase");
        } else {
          params.delete("walk");
        }
        return params;
      },
      { replace: !next },
    );
  };

  // Keys 1, 2 and 3 select an idea and Enter picks it (DESIGN.md "The Idea
  // Card"), unless focus is in a field or on a control that owns Enter.
  // Only while the board itself is on the page: not under Write your own idea
  // or the editor for one idea's words.
  const editingWordsOpen =
    !!selected &&
    editingWords?.slotId === selected.id &&
    canEdit &&
    sortedIdeas.some((idea) => idea.id === editingWords.ideaId && idea.status !== "WORKING");
  const ideaKeysLive =
    canEdit && selected?.status === "IDEATION" && !reopenedIdea && sortedIdeas.length > 0 && !writingOwn && !editingWordsOpen;
  const selectedId = selected?.id;
  useEffect(() => {
    if (!ideaKeysLive || !selectedId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true'], [role='dialog']")) return;
      if (event.key === "Enter") {
        if (!pickedIdeaId || acceptIdea.isPending) return;
        if (target?.closest("button, a, [role='option'], [role='menuitem'], [role='tab'], [role='radio']")) return;
        event.preventDefault();
        pickIdea(selectedId, pickedIdeaId);
        return;
      }
      const index = ["1", "2", "3"].indexOf(event.key);
      const idea = index >= 0 ? sortedIdeas[index] : undefined;
      if (!idea) return;
      setSelectedIdea({ slotId: selectedId, ideaId: idea.id });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // pickIdea is rebuilt every render; re-listening is cheap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideaKeysLive, selectedId, sortedIdeas, pickedIdeaId, acceptIdea, walking, ordered]);

  // Redirect happens in the chat: the idea is pinned to the rail's composer
  // ("Replying to Idea 2") and sending runs that idea's modify. Leaving the
  // post takes the pin with it.
  const replyKey = selected ? `ideas:${selected.id}` : null;
  useEffect(() => {
    if (!replyKey) return;
    return () => agentRail.clearReply(replyKey);
  }, [replyKey]);
  const redirectInChat = (ideaId: string): boolean => {
    if (!selected || !replyKey) return false;
    const index = sortedIdeas.findIndex((idea) => idea.id === ideaId);
    const idea = sortedIdeas[index];
    if (!idea) return false;
    const slotId = selected.id;
    return agentRail.replyTo({
      key: replyKey,
      // Figma 08 "redirect one idea, in the chat": the quote chip names it.
      label: `Redirect idea ${index + 1}`,
      quote: idea.title,
      placeholder: `Where should idea ${index + 1} go?`,
      onSend: (instruction) => {
        markRework(ideaId, "chat");
        ideation.modifyIdea.mutate({ slotId, ideaId, instruction });
      },
    });
  };

  // Write your own idea (Figma 08): your words become the pick as written,
  // beside the three, and Creo writes the brief from them. It goes where the
  // idea on the slide would go. The form closes at once; a refusal reopens it
  // with the draft intact.
  // On a post with no ideas (manualDraft) the same words become its one idea.
  const submitWriteOwn = (draft: WriteOwnDraft, where: "beside" | "alone" = "beside") => {
    if (!selected) return;
    const slotId = selected.id;
    const lead = sortedIdeas.find((idea) => idea.id === pickedIdeaId) ?? sortedIdeas[0];
    const close = where === "alone" ? () => setManualDraft(null) : () => setWriteOwn(null);
    const reopen = where === "alone" ? () => setManualDraft({ slotId, draft }) : () => setWriteOwn({ slotId, draft });
    close();
    ideation.createManualIdea.mutate(
      {
        slotId,
        fields: {
          title: draft.title,
          synopsis: draft.description,
          hooks: draft.hooks,
          ...(draft.objective ? { objectiveClaim: draft.objective } : {}),
          channels: lead?.channels ?? [],
          ...(draft.references.length ? { references: draft.references.map((choice) => choice.pick) } : {}),
        },
        reference: draft.references[0] ? referenceFromChoice(draft.references[0]) : null,
      },
      { onError: reopen },
    );
  };

  const actions: EditorialConceptActions | undefined =
    canEdit && selected?.status === "IDEATION"
      ? {
          onAccept: (ideaId) => ideation.acceptIdea.mutate({ slotId: selected.id, ideaId }),
          onRegenerate: (ideaId) => {
            if (editedIdeaIds.has(ideaId)) setConfirmAngle({ slotId: selected.id, ideaId });
            else {
              markRework(ideaId, "angle");
              ideation.regenerateIdea.mutate({ slotId: selected.id, ideaId });
            }
          },
          onModify: (ideaId, instruction) =>
            ideation.modifyIdea.mutate({ slotId: selected.id, ideaId, instruction }),
          onRedirect: redirectInChat,
          onEditWords: (ideaId) => setEditingWords({ slotId: selected.id, ideaId }),
          // The header carries Write your own on desktop and tablet.
          onWriteOwn: viewport === "phone" ? () => setWriteOwn({ slotId: selected.id, draft: EMPTY_WRITE_OWN }) : undefined,
          onEditIdea: (ideaId, fields: { channels?: CalendarPlatform[] }) =>
            ideation.updateIdea.mutate({ slotId: selected.id, ideaId, fields }),
          onChangeMedia: setMediaIdeaId,
          onRepitchAll: () => ideation.generateIdeas.mutate({ slotId: selected.id }),
          onCancelRework: (ideaId) =>
            ideation.cancelIdeaRework.mutate({ slotId: selected.id, ideaId }),
        }
      : undefined;

  const runPrimary = (action: PrimaryAction) => {
    if (!selected) return;
    switch (action) {
      case "generate-ideas":
      case "retry-ideas":
        ideation.generateIdeas.mutate({ slotId: selected.id });
        return;
      case "pick-idea":
        if (pickedIdeaId) pickIdea(selected.id, pickedIdeaId);
        return;
      case "rework-idea": {
        // The existing revise flow: agree the direction, then revise. A post
        // reopened in IDEATION has no direction box; go to its idea instead.
        const section =
          document.getElementById("revision-direction") ?? document.getElementById("post-concept-work");
        section?.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "center",
        });
        document.getElementById("revision-direction-input")?.focus({ preventScroll: true });
        return;
      }
      case "send-concept":
        onShare(selected.id);
        return;
      case "retry-brief":
        if (fullBrief) retryBrief.mutate(fullBrief.id);
        return;
      case "send-brief":
        setSendBriefOpen(true);
        return;
      case "edit-brief":
        // Already on the brief: keep the count climbing (changePhase resets it,
        // and a request equal to the last one would not open the editor again).
        if (activePhase !== "brief") changePhase("brief");
        setViewing(null);
        setEditBriefRequest((previous) => ({
          slotId: selected.id,
          n: previous?.slotId === selected.id ? previous.n + 1 : 1,
        }));
        return;
      case "open-live":
        changePhase("publishing");
        return;
      case "add-files":
        // The picker lives on the Live tab; from another tab (or Upload), go there first.
        if (activePhase === "publishing") liveStage?.openPicker();
        else changePhase("publishing");
        return;
      case "send-kit":
        liveStage?.sendKit?.();
        return;
      case "copy-kit-link":
        liveStage?.copyKitLink?.();
        return;
      case "move-post":
        setMoveOpen(true);
        return;
      case "remind": {
        const roundId = selected.clientReview?.concept?.roundId;
        if (!roundId || roundId === "pending") return;
        const input = { slotId: selected.id, kind: "concept" as const, roundId };
        remind.mutate(input, {
          onError: (err) => {
            const cooldown = isReminderCooldown(err);
            if (!cooldown) return;
            queue.add({ title: cooldown, action: { label: "Remind anyway", onPress: () => remind.mutate({ ...input, force: true }) } });
          },
        });
        return;
      }
      case "approve-for-client":
        setApproveForOpen(true);
        return;
      case "schedule-all":
        liveStage?.scheduleAll?.();
        return;
      case "mark-posted":
      case "reconnect":
        // Both live on the Live tab, on the channel's own row.
        changePhase("publishing");
        return;
      case "retry-post": {
        // The channel that didn't post, tried again now (its row's Try again).
        const failed = liveStage?.data.posts.find((post) => post.route?.state === "failed" && !post.liveSince);
        if (failed && liveStage?.retryPost) liveStage.retryPost(failed.channelId);
        else changePhase("publishing");
        return;
      }
    }
  };

  const primaryAction = banner?.primary?.action;
  const primaryBusy =
    primaryAction === "generate-ideas" || primaryAction === "retry-ideas"
      ? ideation.generateIdeas.isPending
      : primaryAction === "pick-idea"
        ? ideation.acceptIdea.isPending
        : primaryAction === "retry-brief"
          ? retryBrief.isPending
          : primaryAction === "send-brief"
            ? sendBriefs.isPending
            : false;

  // Change media's confirm: the pick, painted on the slide at once (null is None).
  const confirmMedia = (choice: MediaChoice | null) => {
    if (!selected || !mediaIdea) return;
    ideation.replaceIdeaMedia.mutate({
      slotId: selected.id,
      ideaId: mediaIdea.id,
      media: choice ? choice.pick : { kind: "none" },
      preview: choice ? referenceFromChoice(choice) : null,
    });
  };

  const phone = viewport === "phone";
  // The ideas pages have no banner (postState showBanner): the header carries
  // Write your own and the primary on desktop and tablet, the phone's bottom
  // bar the primary. A failed run's Try again stays in its centred state, and
  // nothing shows while Write your own or the words editor is open.
  const bannerless = !!selectedState && !selectedState.showBanner && !briefWriting;
  const headerPrimary =
    bannerless && !writingOwn && !editingWordsOpen && banner?.primary?.action !== "retry-ideas"
      ? banner?.primary
      : undefined;
  const reworking = sortedIdeas.some((idea) => idea.status === "WORKING");
  const headerWriteOwn =
    bannerless && canEdit && !!selected && selected.status === "IDEATION" && !reopenedIdea && sortedIdeas.length > 0 && !writingOwn && !editingWordsOpen;
  const headerActions =
    !phone && (headerPrimary || headerWriteOwn) ? (
      <>
        {headerWriteOwn && selected ? (
          <Button
            size="small"
            variant="secondary"
            buttonStyle="outline"
            icon={PenLine}
            iconPosition="right"
            iconClassName="size-3.5"
            isDisabled={reworking}
            onPress={() => setWriteOwn({ slotId: selected.id, draft: EMPTY_WRITE_OWN })}
          >
            Write yours
          </Button>
        ) : null}
        {headerPrimary ? (
          <Button
            size="small"
            icon={headerPrimary.action === "pick-idea" ? Check : undefined}
            iconPosition="right"
            iconClassName="size-3.5"
            loading={primaryBusy}
            isDisabled={headerPrimary.disabled || primaryBusy}
            onPress={() => runPrimary(headerPrimary.action)}
          >
            {/* Figma 08: "Pick idea 3", the idea on the slide. */}
            {headerPrimary.label}
          </Button>
        ) : null}
      </>
    ) : undefined;
  return (
    <DashboardPage className="co-post-page overflow-visible">
      {/* Edge to edge of the canvas like the Pen frames (co-post-page cancels
          the shell's gutters); the column pads itself back to the Pen's 32
          (tablet 24) and the header reaches out again for its full-width rule. */}
      <div className="flex min-h-full w-full flex-col bg-co-ground text-co-text">
        {viewport === "tablet" ? (
          // Bar / Top lane on a tablet (Pen: Tablet · pick one of three ideas).
          <div className="px-3">
            <CalendarTopLane tablet brandName={clientName} />
          </div>
        ) : null}
        {ordered.length === 0 || !selected || !selectedState || !banner ? (
          <EmptyConcepts onBack={onBack} />
        ) : (
          <>
            {phone ? null : (
              // The island sits 12 in from the page edges and stays put; the
              // sticky box is this wrapper: it spans the page, so the island
              // holds at the top while the post (banner included) scrolls under it.
              <div className="sticky top-0 z-[21] px-3 pt-3">
                <DateRail
                  monthLabel={`${monthName} ${year}`}
                  countLine={monthCountLine(ordered.length, needYou)}
                  slots={ordered}
                  selectedId={selected.id}
                  states={states}
                  onSelect={onSelect}
                  onBack={onBack}
                  audience={isGuest ? "client" : "team"}
                />
              </div>
            )}

            <div className={cn("flex flex-col", viewport === "tablet" ? "px-6" : phone ? "px-4 pb-24" : "px-8")}>
              <PostHeader
                inset
                title={selected.title}
                viewport={viewport}
                phase={activePhase}
                onPhaseChange={changePhase}
                conceptApproved={approved}
                briefApproved={briefAccepted}
                briefExists={briefExists}
                showDistribution={showDistribution}
                hideConcept={direct}
                // The numbers are the team's; the client reads their Kit.
                versions={isGuest ? [] : versionOptions}
                currentVersion={fullBrief?.version ?? null}
                viewingVersion={viewingVersion}
                onPickVersion={(version) => {
                  setViewing(version === null ? null : { slotId: selected.id, version });
                  if (version !== null && activePhase !== "brief") onPhaseChange("brief");
                }}
                onMove={movable ? () => setMoveOpen(true) : undefined}
                onDelete={canDelete ? () => setDeleteOpen(true) : undefined}
                deleteBlocked={deleteBlocked}
                onPrint={canPrintBrief ? () => void printBrief() : undefined}
                onPreviewAsClient={isGuest || direct ? undefined : () => onPreviewAsClient(selected.id)}
                previewLabel={`Preview as ${clientName?.trim() || "the client"}`}
                ideaActions={(() => {
                  // Desktop and tablet: the slide has no per-idea menu, so the ⋯ carries the moves on the idea on it.
                  if (!actions || phone || !pickedIdeaId) return [];
                  const index = sortedIdeas.findIndex((idea) => idea.id === pickedIdeaId);
                  const idea = sortedIdeas[index];
                  if (!idea || idea.status === "WORKING") return [];
                  const n = index + 1;
                  return [
                    { id: "redirect", label: `Redirect idea ${n} in the chat`, onPress: () => void actions.onRedirect?.(idea.id) },
                    { id: "angle", label: `A new angle on idea ${n}`, onPress: () => actions.onRegenerate?.(idea.id) },
                    { id: "words", label: `Edit idea ${n}’s words`, onPress: () => actions.onEditWords?.(idea.id) },
                    { id: "media", label: idea.reference ? `Change idea ${n}’s media` : `Add media to idea ${n}`, onPress: () => actions.onChangeMedia?.(idea.id) },
                    { id: "again", label: "Write all three again", onPress: () => actions.onRepitchAll?.() },
                  ];
                })()}
                onEditIdea={
                  canEdit && !isGuest && selected.status === "SIGNED_OFF" && !!winner
                    ? () => {
                        if (activePhase !== "concept") onPhaseChange("concept");
                        setEditIdeaRequest((previous) => ({
                          slotId: selected.id,
                          n: previous?.slotId === selected.id ? previous.n + 1 : 1,
                        }));
                      }
                    : undefined
                }
                // The brief's hand edit, whenever the brief can change (the banner only
                // carries it after a decline): the brief tab opens on the editor.
                onEditBrief={
                  canEdit &&
                  !isGuest &&
                  !direct &&
                  !!fullBrief &&
                  !!liveBody(fullBrief) &&
                  (BRIEF_EDITABLE_STATUSES as string[]).includes(fullBrief.status) &&
                  (PLAN_BRIEF_EDITABLE_STATUSES as string[]).includes(plan.status)
                    ? () => runPrimary("edit-brief")
                    : undefined
                }
                printBusy={exporting}
                // Figma 08 ⋯ menu: Pull back from {client}, idea and brief together, after a confirm.
                onRecall={
                  selected.status === "IN_CLIENT_REVIEW" && onRecall && canEdit && !isGuest
                    ? () => setPullBackOpen(true)
                    : canRecallBrief
                      ? () => recallBrief.mutate()
                      : undefined
                }
                recallLabel={`Pull back from ${clientName?.trim() || "the client"}`}
                recallBusy={recallBrief.isPending || !!recalling}
                phoneTop={
                  phone
                    ? {
                        monthLabel: monthName,
                        dateLabel: shortDate(selected.scheduledAt) ?? formatSlotSchedule(selected.scheduledAt),
                        meta: phoneMeta(selected, selectedState, isGuest),
                        onBack,
                      }
                    : undefined
                }
                comments={commentsEntry}
                pageActions={headerActions}
              />

              {bannerless ? (
                phone && headerPrimary ? (
                  // Figma 08 (13 Oct): the phone bottom bar is solid with a 1px top line, nothing shows through.
                  <div className="co-post-bottombar fixed inset-x-0 bottom-0 z-30 border-t border-co-line bg-[var(--co-chrome-solid)]">
                    <Button
                      size="small"
                      loading={primaryBusy}
                      isDisabled={headerPrimary.disabled || primaryBusy}
                      onPress={() => runPrimary(headerPrimary.action)}
                      className="h-11 w-full shrink-0 text-[14px]"
                    >
                      {headerPrimary.label}
                    </Button>
                  </div>
                ) : null
              ) : (
                <StatusBanner
                  className="mt-5"
                  banner={banner}
                  titleSince={activePhase === "brief" ? null : bannerSince}
                  metaSince={activePhase === "brief" ? bannerSince : null}
                  viewport={viewport}
                  busy={primaryBusy}
                  onPrimary={() => {
                    if (banner.primary) runPrimary(banner.primary.action);
                  }}
                  onSecondary={() => {
                    if (banner.secondary) runPrimary(banner.secondary.action);
                  }}
                  secondary={
                    // The idea and its brief are one answer: on record from the banner when late,
                    // and only the legacy brief loop keeps its own team approval here.
                    teamGate === "brief" ? (
                      <Button
                        size="small"
                        variant="secondary"
                        buttonStyle="outline"
                        isDisabled={teamApproveBusy}
                        onPress={() => setTeamApproveFor(selected.id)}
                        className="shrink-0"
                      >
                        Approve without {client}
                      </Button>
                    ) : undefined
                  }
                />
              )}

              {answeredFirstLine ? (
                <p role="status" className="mt-2 text-[13px] leading-[1.45] text-co-muted">
                  {answeredFirstLine}
                </p>
              ) : null}

              {missedDay && !isGuest && ((selected.ideas?.length ?? 0) > 0 || canDelete) ? (
                <div className="mt-3 flex gap-5 pl-0.5">
                  {(selected.ideas?.length ?? 0) > 0 ? (
                    <button
                      type="button"
                      aria-expanded={missedIdeasShown}
                      onClick={() => setMissedIdeasOpen(missedIdeasShown ? null : selected.id)}
                      className="text-[13px] font-medium text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
                    >
                      {missedIdeasShown ? "Hide the three ideas" : "See the three ideas"}
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      disabled={deleteBlocked}
                      onClick={() => setDeleteOpen(true)}
                      className="text-[13px] font-medium text-co-alert-ink outline-none transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:opacity-40"
                    >
                      Delete the post
                    </button>
                  ) : null}
                </div>
              ) : null}

              {/* The idea the brief is written from, while Creo writes it (thWTL); a
                  written brief stands under its banner alone (EIrjn). */}
              {activePhase === "brief" && winner && !isGuest && winnerIndex >= 0 &&
              (!briefBySlot.get(selected.id) || briefBySlot.get(selected.id)?.status === "GENERATING") ? (
                <IdeaReferenceCard
                  className={bannerless ? "mt-6" : "mt-1"}
                  index={winnerIndex}
                  title={winner.title}
                  phone={phone}
                  onOpen={() => changePhase("concept")}
                />
              ) : null}

              {phone && commentsEntry ? (
                <button
                  type="button"
                  onClick={commentsEntry.onOpen}
                  className="mt-3 inline-flex h-9 items-center gap-2 self-start text-[13px] font-medium text-co-muted outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
                >
                  <MessageSquare className="size-4" strokeWidth={1.8} aria-hidden="true" />
                  {comments.count === 0 ? "Comments" : comments.count === 1 ? "1 comment" : `${comments.count} comments`}
                </button>
              ) : null}

              <PostComments
                data={comments}
                enabled={!isGuest}
                viewport={viewport}
                clientName={clientName}
                phase={activePhase}
                briefExists={briefExists}
                brief={fullBrief}
                slotId={selected.id}
                sheetOpen={commentsOpen}
                onSheetOpenChange={setCommentsOpen}
              />

              <main
                id="post-concept-work"
                className={cn(
                  "pb-12",
                  // The Pen's gaps under the header: the ideas 24 (28 under a
                  // banner), the cards being written 16, Write your own 36.
                  activePhase === "brief" && winner && !isGuest && winnerIndex >= 0 ? "pt-[18px]" : writingOwn ? "pt-9" : selected.status === "GENERATING_IDEAS" && !bannerless ? "pt-7" : selected.status === "GENERATING_IDEAS" ? "pt-4" : bannerless ? "pt-6" : "pt-7",
                )}
              >
                {phasePending ? (
                  activePhase === "brief" ? <ProductionBriefSkeleton /> : <LiveSkeleton viewport={viewport} />
                ) : selected.origin === "DIRECT" ? (
                  liveStage ? (
                    <LiveView
                      key={selected.id}
                      slotId={selected.id}
                      stage={liveStage}
                      viewport={viewport}
                      clientName={clientName?.trim() || "the client"}
                      canEdit={canEdit}
                      downloadName={selected.title}
                      onEditPlatforms={editPlatforms}
                      comments={(postCount, desk) => (
                        <LiveComments slotId={selected.id} canReply={canEdit} desk={desk} postCount={postCount} />
                      )}
                    />
                  ) : null
                ) : winner || openDecline ? (
                  <IdeaSlideContext.Provider
                    value={{
                      brandName: clientName?.trim() || "",
                      scheduledAt: selected.scheduledAt,
                      pillarName: selected.pillarName || null,
                      pillarHue: selectedPillarHue ?? null,
                    }}
                  >
                  <ConceptItemWorkspace
                    key={selected.id}
                    plan={plan}
                    slot={selected}
                    canEdit={canEdit}
                    phase={activePhase}
                    liveAllowed={showDistribution}
                    onPhaseChange={changePhase}
                    onPreview={() => onPreviewAsClient(selected.id)}
                    onChangeMedia={setMediaIdeaId}
                    pillarHue={selectedPillarHue}
                    editBriefRequest={editBriefRequest?.slotId === selected.id ? editBriefRequest.n : 0}
                    editIdeaRequest={editIdeaRequest?.slotId === selected.id ? editIdeaRequest.n : 0}
                    readOnlyRevision={readOnlyRevision}
                    onExitRevision={() => setViewing(null)}
                    liveView={
                      liveStage ? (
                        <LiveView
                          key={selected.id}
                          slotId={selected.id}
                          stage={liveStage}
                          viewport={viewport}
                          clientName={clientName?.trim() || "the client"}
                          canEdit={canEdit}
                          downloadAll={isGuest}
                          downloadName={winner?.title || selected.title}
                          onEditPlatforms={editPlatforms}
                          comments={
                            isGuest
                              ? undefined
                              : (postCount, desk) => (
                                  <LiveComments slotId={selected.id} canReply={canEdit} desk={desk} postCount={postCount} />
                                )
                          }
                        />
                      ) : null
                    }
                  />
                  </IdeaSlideContext.Provider>
                ) : null}

                {selected.origin === "DIRECT" ? null : isGuest ? (
                  // The client never sees the team's ideation (proposals, a
                  // rework being written); only what was sent to them, above.
                  winner || openDecline ? null : (
                    <p role="status" className="tt-read-m border-y border-co-line2 py-6 text-co-muted">
                      The team is preparing this post. It appears here when it is ready for your approval.
                    </p>
                  )
                ) : selected.status === "GENERATING_IDEAS" ? (
                  phone ? (
                    <EditorialConceptSkeletons viewport={viewport} writing={{ since: selected.updatedAt }} />
                  ) : (
                    <IdeaSlideBoardWriting since={selected.updatedAt} />
                  )
                ) : selected.status === "IDEATION" && !reopenedIdea && (selected.ideas?.length ?? 0) > 0 ? (
                  <div className={openDecline ? "mt-6" : undefined}>
                    {writingOwn && writeOwn && phone ? (
                      <WriteOwnIdeaPanel
                        key={selected.id}
                        slot={selected}
                        brandId={brandId}
                        phone
                        initial={writeOwn.draft}
                        busy={ideation.createManualIdea.isPending}
                        onCancel={() => setWriteOwn(null)}
                        onSubmit={submitWriteOwn}
                      />
                    ) : editingWordsOpen && editingWords ? (
                      <ConceptDraftEditor
                        key={editingWords.ideaId}
                        idea={sortedIdeas.find((idea) => idea.id === editingWords.ideaId)}
                        heading={`Edit idea ${sortedIdeas.findIndex((idea) => idea.id === editingWords.ideaId) + 1}`}
                        hint="Your words replace the agent's on this idea. Nothing goes to the client until you send it."
                        saving={ideation.updateIdea.isPending}
                        onCancel={() => setEditingWords(null)}
                        onSave={async (fields) => {
                          const ideaId = editingWords.ideaId;
                          await ideation.updateIdea.mutateAsync({ slotId: selected.id, ideaId, fields });
                          setEditedIdeaIds((previous) => new Set(previous).add(ideaId));
                        }}
                      />
                    ) : !phone ? (
                      // Figma 08, 3 Ideas: one idea as one slide, the three as a strip, Write yours.
                      <IdeaSlideBoard
                        ideas={sortedIdeas}
                        slideFor={slideFor}
                        selectedIdeaId={pickedIdeaId}
                        onSelectIdea={
                          actions
                            ? (ideaId) => {
                                setWriteOwn(null);
                                setSelectedIdea({ slotId: selected.id, ideaId });
                              }
                            : undefined
                        }
                        onChangeMedia={actions ? setMediaIdeaId : undefined}
                        onWriteOwn={actions ? () => setWriteOwn({ slotId: selected.id, draft: EMPTY_WRITE_OWN }) : undefined}
                        rewriting={(() => {
                          const busy = sortedIdeas.find((idea) => idea.status === "WORKING");
                          return busy ? { ideaId: busy.id, since: busy.updatedAt, from: reworkFrom[busy.id] ?? null } : null;
                        })()}
                        readOnly={!actions}
                        own={
                          writingOwn && writeOwn ? (
                            <WriteOwnIdeaPanel
                              key={selected.id}
                              slot={selected}
                              brandId={brandId}
                              phone={false}
                              initial={writeOwn.draft}
                              busy={ideation.createManualIdea.isPending}
                              onCancel={() => setWriteOwn(null)}
                              onSubmit={submitWriteOwn}
                            />
                          ) : undefined
                        }
                      />
                    ) : (
                      <EditorialConceptBoard
                        slot={selected}
                        actions={actions}
                        selectedIdeaId={pickedIdeaId}
                        onSelectIdea={
                          actions ? (ideaId) => setSelectedIdea({ slotId: selected.id, ideaId }) : undefined
                        }
                        onPickIdea={actions ? (ideaId) => pickIdea(selected.id, ideaId) : undefined}
                        phoneStory={{
                          title: selected.title,
                          dateLabel: shortDate(selected.scheduledAt) ?? formatSlotSchedule(selected.scheduledAt),
                          onBack,
                        }}
                        viewport={viewport}
                        clientName={clientName}
                        reworkFrom={reworkFrom}
                      />
                    )}
                  </div>
                ) : selected.status === "MISSED" && (selected.ideas?.length ?? 0) > 0 && !winner ? (
                  // The day passed: the three ideas stay here, dimmed and
                  // read-only, behind See the three ideas; Move is the way on.
                  missedIdeasShown ? (
                    <EditorialConceptBoard slot={selected} viewport={viewport} clientName={clientName} readOnly />
                  ) : null
                ) : selected.status === "PLANNED" && selected.ideaError && !openDecline && manualDraft?.slotId !== selected.id ? (
                  // Ideas didn't come (HIjRU): in the Pen the chat carries the
                  // failed run; the page keeps the same words and Try again in a
                  // centred card so the way on never depends on the rail.
                  <div className="flex justify-center px-4 pt-[120px]">
                    <div
                      role="status"
                      className="flex w-full max-w-[420px] flex-col items-center gap-2 rounded-card bg-co-surface-solid px-7 py-[22px] text-center"
                    >
                      <TriangleAlert className="size-5 text-co-alert-ink" strokeWidth={1.8} aria-hidden="true" />
                      <p className="text-[15px] font-semibold leading-snug text-co-text">Ideas didn’t come</p>
                      <p className="text-[13px] leading-[1.5] text-co-text2">
                        {`The three ideas for ${shortDate(selected.scheduledAt) ?? "this post"} could not be finished. Nothing else about the post changed.`}
                        {shortReason(selected.ideaError) ? ` ${shortReason(selected.ideaError)}.` : ""}
                      </p>
                      {canEdit ? (
                        <div className="mt-2 flex flex-wrap justify-center gap-2.5">
                          <Button
                            size="small"
                            isDisabled={ideation.generateIdeas.isPending}
                            onPress={() => ideation.generateIdeas.mutate({ slotId: selected.id })}
                          >
                            Try again
                          </Button>
                          <Button
                            size="small"
                            variant="secondary"
                            buttonStyle="outline"
                            icon={PenLine}
                            iconPosition="right"
                            iconClassName="size-3.5"
                            onPress={() => setManualDraft({ slotId: selected.id, draft: EMPTY_WRITE_OWN })}
                          >
                            Write yours
                          </Button>
                        </div>
                      ) : (
                        <p className="text-[13px] leading-[1.5] text-co-muted">The team will try again.</p>
                      )}
                    </div>
                  </div>
                ) : winner || openDecline ? null : manualDraft?.slotId === selected.id && selected.status === "PLANNED" && canEdit ? (
                  // The same form as beside the three ideas (Figma 08), in the slide's place.
                  <div className={phone ? undefined : "bg-co-surface px-6 pb-12 pt-9 sm:px-[58px]"} style={phone ? undefined : { maxWidth: 1160 }}>
                    <WriteOwnIdeaPanel
                      key={selected.id}
                      slot={selected}
                      brandId={brandId}
                      phone={phone}
                      initial={manualDraft.draft}
                      backLabel="Back"
                      busy={ideation.createManualIdea.isPending}
                      onCancel={() => setManualDraft(null)}
                      onSubmit={(draft) => submitWriteOwn(draft, "alone")}
                    />
                  </div>
                ) : (
                  // The Centred State Rule: glyph, title, one line, the action,
                  // centred in the space the ideas will take. No rules of its own.
                  <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-4 py-12 text-center">
                    <span className="grid size-9 place-items-center rounded-[4px] bg-co-tonal text-co-text">
                      {selected.status === "MISSED" ? (
                        <CalendarX className="size-5" strokeWidth={1.5} aria-hidden="true" />
                      ) : (
                        <Sparkles className="size-5" strokeWidth={1.5} aria-hidden="true" />
                      )}
                    </span>
                    <h2 className="font-section text-[22px] font-extrabold leading-tight tracking-normal text-co-text">
                      {selected.status === "MISSED" ? "No idea was picked" : "No ideas yet"}
                    </h2>
                    <p className="max-w-[380px] text-[14px] leading-relaxed text-co-muted">
                      {selected.status === "MISSED"
                        ? "The day passed before an idea was picked."
                        : canEdit
                          ? "Creo writes three when you press Write ideas, or write your own."
                          : "The team has not written ideas for this post yet."}
                    </p>
                    {selected.status === "PLANNED" && canEdit ? (
                      <Button
                        size="small"
                        variant="secondary"
                        buttonStyle="outline"
                        className="mt-2"
                        isDisabled={ideation.generateIdeas.isPending}
                        onPress={() => setManualDraft({ slotId: selected.id, draft: EMPTY_WRITE_OWN })}
                      >
                        Write your own idea
                      </Button>
                    ) : null}
                  </div>
                )}
              </main>
            </div>
          </>
        )}

        {selected && mediaIdea ? (
          <IdeaMediaDialog
            brandId={brandId}
            isOpen
            onClose={() => setMediaIdeaId(null)}
            ideaLabel={`Idea ${sortedIdeas.findIndex((idea) => idea.id === mediaIdea.id) + 1}, ${shortIdeaName(mediaIdea.title)}`}
            plannedRatio={displayAspectRatio(selected)}
            current={mediaIdea.reference}
            sources={mediaIdea.sources}
            onConfirm={confirmMedia}
          />
        ) : null}

        {pullBackOpen && selected && onRecall ? (
          <KitModal
            isOpen
            onOpenChange={(open) => {
              if (!open) setPullBackOpen(false);
            }}
            title={`Pull back from ${clientName?.trim() || "the client"}?`}
            modalClassName="max-w-[440px]"
          >
            <p className="text-[14px] leading-relaxed text-co-text2">It leaves their page. They were not told, so there is nothing to explain.</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button size="small" variant="secondary" buttonStyle="outline" autoFocus onPress={() => setPullBackOpen(false)}>
                Keep it
              </Button>
              <Button
                size="small"
                onPress={() => {
                  setPullBackOpen(false);
                  onRecall(selected.id);
                }}
              >
                Pull back
              </Button>
            </div>
          </KitModal>
        ) : null}

        {confirmAngle ? (
          <KitModal
            isOpen
            onOpenChange={(open) => {
              if (!open) setConfirmAngle(null);
            }}
            title="Replace your edits?"
            modalClassName="max-w-[440px]"
          >
            <p className="text-[14px] leading-relaxed text-co-text2">
              A new angle rewrites this idea, and the words you edited are replaced.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button size="small" variant="secondary" buttonStyle="outline" autoFocus onPress={() => setConfirmAngle(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="error"
                onPress={() => {
                  const { slotId, ideaId } = confirmAngle;
                  setConfirmAngle(null);
                  setEditedIdeaIds((previous) => {
                    const next = new Set(previous);
                    next.delete(ideaId);
                    return next;
                  });
                  markRework(ideaId, "angle");
                  ideation.regenerateIdea.mutate({ slotId, ideaId });
                }}
              >
                Write a new angle
              </Button>
            </div>
          </KitModal>
        ) : null}

        {approveForOpen && selected ? (
          <KitModal
            isOpen
            onOpenChange={(open) => {
              if (!open) setApproveForOpen(false);
            }}
            title={`Approve for ${Client}?`}
            modalClassName="max-w-[560px]"
          >
            <p className="text-[14px] leading-relaxed text-co-text2">
              {(() => {
                const due = shortDate(selected.clientReview?.concept?.answerBy ?? null);
                const missed = due ? `${Client} did not answer by ${due}.` : `${Client} has not answered.`;
                return `${missed} This is recorded as: Approved by ${myName ?? "you"}, ${missed.replace(/.$/, "")}.`;
              })()}
            </p>
            <button
              type="button"
              onClick={() => {
                setApproveForOpen(false);
                setRecordOutsideOpen(true);
              }}
              className="mt-3 text-[13px] font-medium text-co-accent-ink outline-none hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
            >
              They approved outside Creo? Record it
            </button>
            <div className="mt-6 flex justify-end gap-2">
              <Button size="small" variant="secondary" buttonStyle="outline" autoFocus onPress={() => setApproveForOpen(false)}>
                Cancel
              </Button>
              <Button
                size="small"
                onPress={() => {
                  setApproveForOpen(false);
                  teamApproveSlot.mutate(selected.id, noteAnsweredFirst(selected.id));
                }}
              >
                Approve for {Client}
              </Button>
            </div>
          </KitModal>
        ) : null}

        {selected ? (
          <RecordApprovalDialog
            isOpen={recordOutsideOpen}
            onOpenChange={setRecordOutsideOpen}
            planId={planId}
            slotId={selected.id}
            kind="concept"
            version={fullBrief?.sentVersion ?? 1}
            clientName={clientName}
          />
        ) : null}

        {teamGate && selected && teamApproveFor === selected.id ? (
          <KitModal
            isOpen
            onOpenChange={(open) => {
              if (!open) setTeamApproveFor(null);
            }}
            title={teamGate === "concept" ? `Approve the idea and brief without ${client}?` : `Approve the brief without ${client}?`}
            modalClassName="max-w-[440px]"
          >
            <p className="text-[14px] leading-relaxed text-co-text2">
              {teamGate === "concept"
                ? `${Client} does not review this post. The idea and version ${fullBrief?.version ?? 1} of the creative brief are approved as they stand, and the post moves on to its files.`
                : `Version ${fullBrief?.version ?? 1} of the creative brief is approved as it stands, and the post moves to production.`}
              {(teamGate === "concept" && selected.status === "IN_CLIENT_REVIEW") ||
              (teamGate === "brief" && fullBrief?.status === "IN_REVIEW")
                ? ` It comes off ${client}'s page first. If they have already answered, their answer stands.`
                : teamGate === "brief" && fullBrief?.status === "CHANGES_REQUESTED"
                  ? ` This sets aside the changes ${client} asked for.`
                  : null}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button size="small" variant="secondary" buttonStyle="outline" autoFocus onPress={() => setTeamApproveFor(null)}>
                Cancel
              </Button>
              <Button size="small" onPress={confirmTeamApproval}>
                Approve
              </Button>
            </div>
          </KitModal>
        ) : null}

        {platformsOpen && selected ? (
          <EditPlatformsModal key={selected.id} slot={selected} brandId={brandId} onClose={() => setPlatformsOpen(false)} />
        ) : null}

        {deleteOpen && selected ? (
          <DeleteSlotModal
            slot={selected}
            brief={railBrief}
            clientName={clientName}
            phone={phone}
            // The dialog closes on confirm and the delete waits out its Undo
            // toast, so the mutation's pending flag belongs to a post already
            // gone (it read "Deleting…" on the next post for six seconds).
            loading={false}
            onConfirm={confirmDelete}
            onClose={() => setDeleteOpen(false)}
          />
        ) : null}

        {moveOpen && selected && onMoveSlot ? (
          <MovePostDialog
            slot={selected}
            slots={slots}
            year={year}
            month={month}
            onMove={async (move) => {
              await onMoveSlot(move);
            }}
            onClose={() => setMoveOpen(false)}
          />
        ) : null}

        {sendBriefOpen && selected && fullBrief ? (
          <SendToClientModal
            isOpen
            planId={planId}
            kind="brief"
            items={[{
              slotId: selected.id,
              title: fullBrief.ideaTitle || selected.title,
              meta: formatSlotSchedule(selected.scheduledAt),
              versionLabel: `Version ${fullBrief.version}`,
            }]}
            busy={sendBriefs.isPending}
            onPublish={(slotIds) => sendBriefs.mutateAsync(slotIds).then(() => undefined)}
            onClose={() => setSendBriefOpen(false)}
          />
        ) : null}
      </div>
    </DashboardPage>
  );
}
